<template>
  <LabelBadge :label="statusConfig.label" :color="statusConfig.color" :icon="statusConfig.icon" />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiLockOpenVariant, mdiLock, mdiAlert, mdiCheckCircle, mdiClockTimeThree } from '@mdi/js'

import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import type { LabelBadgeColor } from '@/components/designSystem/Badges/LabelBadge.vue'
import { ExceptionStatusEnum } from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import { DSAttendanceStatus } from '@/constants/ds-attendance.constants'
import { FacialBiometryStatus } from '@/constants/facial-biometry.constants'

type StatusLabelBadgeConfig = {
  label: string
  color: LabelBadgeColor
  icon: string | null
}

const STATUS_CONFIG_MAP: Record<string, StatusLabelBadgeConfig> = {
  [ExceptionStatusEnum.AWAITING_APPROVAL]: {
    label: 'Em andamento',
    color: 'orange',
    icon: mdiLock
  },
  [ExceptionStatusEnum.APPROVED]: {
    label: 'Aprovado',
    color: 'red',
    icon: mdiLockOpenVariant
  },
  [ExceptionStatusEnum.REJECTED]: {
    label: 'Reprovado',
    color: 'blue',
    icon: mdiLock
  },
  [ExceptionStatusEnum.REVERTED]: {
    label: 'Revertido',
    color: 'blue',
    icon: mdiLock
  },
  [ExceptionStatusEnum.CANCELED]: {
    label: 'Cancelado',
    color: 'gray',
    icon: mdiLock
  },
  [DSAttendanceStatus.PENDING_SUBMISSION]: {
    label: 'Aguardando Envio',
    color: 'gray',
    icon: null
  },
  [DSAttendanceStatus.PENDING_AUDIT]: {
    label: 'Aguardando Auditoria',
    color: 'yellow',
    icon: null
  },
  [DSAttendanceStatus.AUDIT_IN_PROGRESS]: {
    label: 'Auditoria em Progresso',
    color: 'blue',
    icon: null
  },
  [DSAttendanceStatus.APPROVED]: {
    label: 'Aprovado',
    color: 'green',
    icon: null
  },
  [DSAttendanceStatus.REJECTED]: {
    label: 'Reprovado',
    color: 'red',
    icon: null
  },
  [DSAttendanceStatus.DELETED]: {
    label: 'Excluído',
    color: 'red',
    icon: null
  },
  [FacialBiometryStatus.NOT_SUBMITTED]: {
    label: 'Sem biometria',
    color: 'red',
    icon: mdiAlert
  },
  [FacialBiometryStatus.PENDING]: {
    label: 'Em validação',
    color: 'yellow',
    icon: mdiClockTimeThree
  },
  [FacialBiometryStatus.VERIFIED]: {
    label: 'Aprovado',
    color: 'green',
    icon: mdiCheckCircle
  },
  [FacialBiometryStatus.REJECTED]: {
    label: 'Reprovado',
    color: 'orange',
    icon: mdiAlert
  }
}

const DEFAULT_CONFIG: StatusLabelBadgeConfig = {
  label: 'Desconhecido',
  color: 'gray',
  icon: mdiLock
}

const props = defineProps<{
  status: ExceptionStatusEnum | DSAttendanceStatus | FacialBiometryStatus
}>()

const statusConfig = computed(() => STATUS_CONFIG_MAP[props.status] ?? DEFAULT_CONFIG)
</script>
